// Revolving-limit panel (spec §13): draw, repay, the drawing switch,
// limit increase, PayLater purchases and collateral top-up.
import { useEffect, useState } from 'react';
import {
  bnplPurchase,
  drawFromLimit,
  increaseLoanLimit,
  pledgeMore,
  repayToLimit,
  setDrawingEnabled,
  getSecurityHoldings,
} from '@/api/loans';
import { ApiError } from '@/api/client';
import type { Holding, LoanRecord } from '@/types/loan.types';
import { BNPL_PARTNERS } from '@/mock/loanData';
import { formatLong } from '@/mock/clock';
import { formatIndianCurrency } from '@/utils/currency';
import Button from '@/components/ui/Button/Button';
import { ErrorLine, inr } from './loanShared';

type Open = 'draw' | 'repay' | 'limit' | 'purchase' | 'pledge' | null;

const RevolvingPanel = ({ loan, onChanged }: { loan: LoanRecord; onChanged: () => void }) => {
  const [open, setOpen] = useState<Open>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [partnerCode, setPartnerCode] = useState(BNPL_PARTNERS[0]?.code ?? '');
  const [purchaseCode, setPurchaseCode] = useState('');
  const [holdings, setHoldings] = useState<Array<Holding & { pledged: boolean }>>([]);
  const [picked, setPicked] = useState<string[]>([]);

  useEffect(() => {
    if (open === 'pledge') {
      getSecurityHoldings()
        .then((h) => setHoldings(h.filter((x) => !x.pledged)))
        .catch(() => setHoldings([]));
    }
  }, [open]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(ok);
      setOpen(null);
      setAmount('');
      setPurchaseCode('');
      setPicked([]);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setBusy(false);
    }
  };

  const available = loan.amountPaise - (loan.usedPaise ?? 0);
  const isOd = loan.productId === 'OVERDRAFT';
  const isBnpl = loan.productId === 'PAYLATER';
  const isSecured = ['LAMF', 'LAS'].includes(loan.productId);

  return (
    <div className="nova-loan-revolving">
      {loan.renewsAt && (
        <p className="nova-loan-note">Limit renews {formatLong(loan.renewsAt)}. Interest runs only on what you use.</p>
      )}

      {isOd && (
        <label className="nova-loan-consent">
          <input
            type="checkbox"
            checked={loan.drawingEnabled ?? false}
            disabled={busy}
            onChange={(e) =>
              run(() => setDrawingEnabled(loan.id, e.target.checked), e.target.checked ? 'Drawing switched on.' : 'Drawing switched off.')
            }
          />
          <span>Drawing switched on — money moves into my account when I draw.</span>
        </label>
      )}

      <div className="nova-loan-actions">
        <button type="button" className="nova-loan-link" onClick={() => setOpen(open === 'draw' ? null : 'draw')}>
          Draw
        </button>
        {(loan.usedPaise ?? 0) > 0 && (
          <button type="button" className="nova-loan-link" onClick={() => setOpen(open === 'repay' ? null : 'repay')}>
            Repay
          </button>
        )}
        <button type="button" className="nova-loan-link" onClick={() => setOpen(open === 'limit' ? null : 'limit')}>
          Increase limit
        </button>
        {isBnpl && (
          <button type="button" className="nova-loan-link" onClick={() => setOpen(open === 'purchase' ? null : 'purchase')}>
            New purchase
          </button>
        )}
        {isSecured && (
          <button type="button" className="nova-loan-link" onClick={() => setOpen(open === 'pledge' ? null : 'pledge')}>
            Pledge more
          </button>
        )}
      </div>

      {open === 'draw' && (
        <div className="nova-loan-panel">
          <h3 className="nova-loan-section">Draw from your limit</h3>
          <p className="nova-loan-note">Up to {inr(available)} available.</p>
          <label className="nova-loan-field">
            <span>Amount (₹)</span>
            <input className="form-control" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
          </label>
          <Button isLoading={busy} disabled={!amount} onClick={() => run(() => drawFromLimit(loan.id, Number(amount)), 'Drawn to your account.')}>
            Draw
          </Button>
        </div>
      )}

      {open === 'repay' && (
        <div className="nova-loan-panel">
          <h3 className="nova-loan-section">Repay</h3>
          <p className="nova-loan-note">
            {inr(loan.usedPaise ?? 0)} outstanding{isBnpl ? ' — payments clear the oldest purchase first' : ''}.
          </p>
          <label className="nova-loan-field">
            <span>Amount (₹)</span>
            <input className="form-control" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
          </label>
          <Button isLoading={busy} disabled={!amount} onClick={() => run(() => repayToLimit(loan.id, Number(amount)), 'Repayment done.')}>
            Repay
          </Button>
        </div>
      )}

      {open === 'limit' && (
        <div className="nova-loan-panel">
          <h3 className="nova-loan-section">Increase your limit</h3>
          <label className="nova-loan-field">
            <span>New limit (₹)</span>
            <input className="form-control" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
          </label>
          <Button isLoading={busy} disabled={!amount} onClick={() => run(() => increaseLoanLimit(loan.id, Number(amount), true), 'Your new limit is live.')}>
            Increase limit
          </Button>
        </div>
      )}

      {open === 'purchase' && isBnpl && (
        <div className="nova-loan-panel">
          <h3 className="nova-loan-section">Pay a store with PayLater</h3>
          <label className="nova-loan-field">
            <span>Partner store</span>
            <select className="form-select" value={partnerCode} onChange={(e) => setPartnerCode(e.target.value)}>
              {BNPL_PARTNERS.map((p) => (
                <option key={p.code} value={p.code}>{p.name} ({p.code})</option>
              ))}
            </select>
          </label>
          <label className="nova-loan-field">
            <span>Purchase code from the store</span>
            <input className="form-control" value={purchaseCode} onChange={(e) => setPurchaseCode(e.target.value)} placeholder="6–12 letters or digits" />
          </label>
          <label className="nova-loan-field">
            <span>Amount (₹)</span>
            <input className="form-control" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
          </label>
          <Button
            isLoading={busy}
            disabled={!amount || !purchaseCode}
            onClick={() => run(() => bnplPurchase(loan.id, partnerCode, purchaseCode, Number(amount)), 'Paid from your PayLater limit.')}
          >
            Pay with PayLater
          </Button>
        </div>
      )}

      {open === 'pledge' && isSecured && (
        <div className="nova-loan-panel">
          <h3 className="nova-loan-section">Pledge more securities</h3>
          {holdings.length === 0 && <p className="nova-loan-note">No unpledged holdings left.</p>}
          {holdings.map((h) => (
            <label key={h.id} className="nova-loan-holding">
              <input
                type="checkbox"
                checked={picked.includes(h.id)}
                onChange={() => setPicked((prev) => (prev.includes(h.id) ? prev.filter((x) => x !== h.id) : [...prev, h.id]))}
              />
              <span className="nova-loan-holding-text">
                <strong>{h.name}</strong>
                <span>{formatIndianCurrency(h.valueRupees)} · lends up to {h.ltvPct}%</span>
              </span>
            </label>
          ))}
          <Button isLoading={busy} disabled={picked.length === 0} onClick={() => run(() => pledgeMore(loan.id, picked), 'Holdings pledged.')}>
            Pledge
          </Button>
        </div>
      )}

      {isBnpl && (loan.purchases ?? []).length > 0 && (
        <>
          <h3 className="nova-loan-section">Purchases</h3>
          {(loan.purchases ?? []).map((p) => (
            <div key={p.id} className="nova-loan-row is-static">
              <span className="nova-loan-row-text">
                <span className="nova-loan-row-title">{p.partnerName}</span>
                <span className="nova-loan-row-sub">
                  {p.purchaseCode} · {formatLong(p.purchasedAt)}
                </span>
              </span>
              <span className="nova-loan-row-right">
                <strong>{inr(p.outstandingPaise)}</strong>
                {p.refundedPaise > 0 && <span className="nova-loan-row-sub"> refunded {inr(p.refundedPaise)}</span>}
              </span>
            </div>
          ))}
        </>
      )}

      {notice && <p className="nova-loan-ok">{notice}</p>}
      <ErrorLine message={error} />
    </div>
  );
};

export default RevolvingPanel;
